import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { FormModal } from '@/components/ui/form-modal'
import { FormSection } from '@/components/ui/form-section'
import { SelectFieldControl, TextFieldControl } from '@/components/ui/form-fields'

import { useInventoryLocations, useLocationActions } from '../hooks'
import type { CreateLocationBody } from '../types'

/** "Central" as a key the select can hold — an unset select cannot be returned to. */
const CENTRAL = 'central'

const schema = z.object({
  name: z.string().trim().min(1, 'Give the warehouse a name.').max(255, 'At most 255 characters.'),
  branchId: z.string(),
})

type FormValues = z.infer<typeof schema>

interface LocationFormDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onCreated: () => void
}

/**
 * A new warehouse: a name, and the branch it supplies — or none, for a central
 * one that stocks every branch.
 *
 * Only warehouses are made here. Branches come from the business's branches
 * under Business, and the API refuses any other type.
 *
 * Mounted fresh for each opening (the caller keys it), so nothing typed into an
 * abandoned form comes back the next time.
 */
export function LocationFormDialog({ isOpen, onOpenChange, onCreated }: LocationFormDialogProps) {
  const { create } = useLocationActions()
  const { locations } = useInventoryLocations()

  /* One idempotency key per distinct body, as the item form does: a retry of the
     same body replays the first answer, a changed body gets a new key. */
  const [attempt, setAttempt] = useState<{ key: string; body: string } | null>(null)

  const { control, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: { name: '', branchId: CENTRAL },
  })

  // Active branches only: the API refuses a new warehouse for an inactive one.
  const branchOptions = [
    { value: CENTRAL, label: 'Every branch (central)' },
    ...locations
      .filter((location) => location.type === 'branch' && location.branchId)
      .map((location) => ({ value: location.branchId as string, label: location.name })),
  ]

  const submit = handleSubmit(async (values) => {
    const body: CreateLocationBody = { type: 'warehouse', name: values.name.trim() }
    // Left off for a central warehouse: an absent branch already means "none".
    if (values.branchId !== CENTRAL) body.branchId = values.branchId

    const serialised = JSON.stringify(body)
    const next =
      attempt?.body === serialised ? attempt : { key: crypto.randomUUID(), body: serialised }
    setAttempt(next)

    await create.mutateAsync({ body, idempotencyKey: next.key })
    onCreated()
  })

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="New warehouse"
      description="A store room or central kitchen that holds stock. Branches come from Business."
      submitLabel="Create warehouse"
      onSubmit={submit}
      isBusy={create.isPending}
      error={create.error}
      errorFallback="Could not create this warehouse."
      dialogClassName="max-w-2xl"
    >
      <FormSection title="Details">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextFieldControl
            control={control}
            name="name"
            label="Name"
            isRequired
            autoFocus
            description="Must be unique across your locations."
          />
          <SelectFieldControl
            control={control}
            name="branchId"
            label="Supplies"
            isRequired
            options={branchOptions}
          />
        </div>
      </FormSection>
    </FormModal>
  )
}
